import React, { useState } from "react";
import { Button, Radio, Tag, Modal, message } from "antd";
import { useAuth } from "../context/AuthContext";
import { SUBSCRIPTION_CONFIG } from "../config/zibuzzConfig";
import {
  PRICING_DATA,
  BILLING_CYCLES,
  PLAN_TIERS,
  calculatePricing,
  formatCurrency,
  getBillingCycleOptions,
  PLAN_NAMES,
} from "../constants/PricingConstants";
import {
  handleTrialActivation,
  handleSubscriptionFlow
} from "../utils/subscriptionUtils";
import "../styles/subscription.css";

/**
 * Subscription Page Content Component
 * Displays pricing plans within ChatWindow for authenticated users
 */
const SubscriptionPageContent = ({ onOpenSubscriptionModal, userProfile }) => {
  const { token, setToken } = useAuth();
  const [billingCycle, setBillingCycle] = useState(BILLING_CYCLES.MONTHLY.key);

  const isAuthenticated = !!token;
  const cycleOptions = getBillingCycleOptions();

  // Extract current plan tier
  const plan = userProfile?.Plan?.plan?.toLowerCase();

  const currentPlanTier = Object.values(PLAN_TIERS)
    .map(tier => tier.toLowerCase())
    .includes(plan)
    ? plan
    : null;
  const trialUsed = userProfile?.TrialUsed || currentPlanTier === "trial";
  const hasActivePaidPlan = currentPlanTier !== "trial" && new Date(userProfile?.Plan?.expiry) > new Date();

  // Get current billing cycle (simplified - assuming monthly for now)
  const getCurrentBillingCycle = () => {
    if (!currentPlanTier || !userProfile?.Plan?.expiry) {
      return "monthly";
    }
    // TODO: Store billing cycle in Plan object for accurate detection
    return "monthly";
  };

  const currentBillingCycle = getCurrentBillingCycle();


  // eslint-disable-next-line
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  const handlePlanSelect = async (tier) => {
    if (!isAuthenticated) return;

    // Show confirmation modal
    Modal.confirm({
      title: "Confirm Subscription",
      content: (
        <div>
          <p>
            You are subscribing to <strong>{PLAN_NAMES[tier] || tier}</strong> (
            {BILLING_CYCLES[billingCycle.toUpperCase()]?.label || billingCycle})
          </p>
          {tier === PLAN_TIERS.SOLO && calculatePricing(tier, billingCycle) && (
            <p style={{ marginTop: 8 }}>
              <strong>Total: {formatCurrency(calculatePricing(tier, billingCycle).finalTotal)}</strong>
            </p>
          )}
          {tier === PLAN_TIERS.GALAXY && calculatePricing(tier, billingCycle) && (
            <p style={{ marginTop: 8 }}>
              <strong>Total: {formatCurrency(calculatePricing(tier, billingCycle).finalTotal)}</strong>
            </p>
          )}
          <p style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
            You will be redirected to the payment gateway.
          </p>
        </div>
      ),
      okText: "Proceed to Pay",
      cancelText: "Cancel",
      onOk: async () => {
        setIsProcessingPayment(true);

        await handleSubscriptionFlow({
          token,
          targetPlan: tier,
          validity: billingCycle,
          userProfile,
          setToken,
          onSuccess: () => {
            setIsProcessingPayment(false);
            // Payment successful - UI will update via token change
          },
          onError: (error) => {
            setIsProcessingPayment(false);
            console.error("Payment flow error:", error);
          },
          onAdjustmentInfo: (adjustInfo) => {
            // Show adjustment info as toast message with longer duration
            message.info({
              content: adjustInfo.message,
              duration: 8, // 8 seconds
              style: {
                marginTop: '20vh',
              },
            });
          },
        });
      },
    });
  };

  const handleActivateTrial = async () => {
    if (currentPlanTier !== null || !token) return;

    await handleTrialActivation({
      token,
      setToken,
      trialDays: SUBSCRIPTION_CONFIG.TRIAL_DAYS,
      onSuccess: () => {
        // Trial activated successfully - UI will update via token change
      },
      onError: (error) => {
        console.error("Trial activation failed:", error);
      },
    });
  };

  return (
    <div className="subscription-page-content">
      {/* Page Header */}
      <div className="subscription-content-header">
        <h1>{hasActivePaidPlan ? "Renew / Change Plan" : "Choose Your Plan"}</h1>
        <p>Simple pricing for creators & businesses</p>
      </div>

      {/* Billing Cycle Selector */}
      <div className="billing-cycle-selector">
        <div className="billing-label">Billing Cycle</div>
        <Radio.Group
          value={billingCycle}
          onChange={(e) => setBillingCycle(e.target.value)}
          size="large"
        >
          {cycleOptions.map((cycle) => (
            <Radio.Button key={cycle.key} value={cycle.key}>
              {cycle.label}
              {cycle.discount > 0 && ` (${cycle.discount}% off)`}
            </Radio.Button>
          ))}
        </Radio.Group>

        {/* Savings Message */}
        {(BILLING_CYCLES.QUARTERLY.key === billingCycle ||
          BILLING_CYCLES.ANNUAL.key === billingCycle) && (
          <div className="billing-savings-message">
            {billingCycle === BILLING_CYCLES.QUARTERLY.key &&
              `Save ${BILLING_CYCLES.QUARTERLY.discount}% with quarterly billing`}
            {billingCycle === BILLING_CYCLES.ANNUAL.key &&
              `Save ${BILLING_CYCLES.ANNUAL.discount}% with annual billing`}
          </div>
        )}
      </div>

      {/* Pricing Cards Grid */}
      <div className="pricing-grid-compact">
        {Object.values(PRICING_DATA)
          .sort((a, b) => (a.planHierarchy || 0) - (b.planHierarchy || 0))
          .map((plan) => (
            <PricingCard
              key={plan.tier}
              plan={plan}
              pricing={calculatePricing(plan.tier, billingCycle)}
              billingCycle={billingCycle}
              onSelect={() => handlePlanSelect(plan.tier)}
              disabled={!isAuthenticated}
              highlight={plan.tier === PLAN_TIERS.GALAXY} // Keeping Galaxy as highlighted for now
              isCurrentPlan={currentPlanTier === plan.tier}
              currentPlanTier={currentPlanTier}
              trialUsed={trialUsed}
              currentBillingCycle={currentBillingCycle}
              onActivateTrial={handleActivateTrial}
            />
          ))}
      </div>
    </div>
  );
};

/**
 * Pricing Card Component
 * Displays detailed pricing information for a single plan
 */
const PricingCard = ({
  plan,
  pricing,
  billingCycle,
  onSelect,
  disabled,
  highlight,
  isCurrentPlan = false,
  currentPlanTier = null,
  trialUsed = false,
  currentBillingCycle = "monthly",
  onActivateTrial,
}) => {
  const [showAllFeatures, setShowAllFeatures] = useState(false);

  if (!pricing) return null;

  const VISIBLE_FEATURES_COUNT = 3;
  const visibleFeatures = showAllFeatures
    ? plan.features
    : plan.features.slice(0, VISIBLE_FEATURES_COUNT);
  const hasMoreFeatures = plan.features.length > VISIBLE_FEATURES_COUNT;

  // Determine button logic
  const canActivateTrial = !trialUsed && plan.trialAvailable;
  const isSamePlanAndCycle =
    isCurrentPlan && currentBillingCycle === billingCycle;

  const getButtonText = () => {
    if (disabled) return "Login to Subscribe";
    if (isSamePlanAndCycle) return "Renew Plan";
    return "Get Started";
  };

  const showTrialButton = canActivateTrial;

  return (
    <div
      className={`pricing-card ${highlight ? "highlight" : ""} ${
        isCurrentPlan ? "current-plan" : ""
      }`}
    >
      {/* Current Plan Badge */}
      {isCurrentPlan && <div className="current-plan-badge">Current Plan</div>}

      {/* Card Header */}
      <div className="price-header">
        {plan.badge && <Tag className="price-badge">{plan.badge}</Tag>}
        <h3>{plan.name}</h3>
        <p className="price-description">{plan.description}</p>
      </div>

      {/* Price Display */}
      <div className="price-display">
        {pricing.hasOffer && (
          <div className="price-default-struck">
            {formatCurrency(pricing.defaultMonthlyPrice)}
          </div>
        )}
        <div className="price-current">
          {formatCurrency(pricing.monthlyPrice)}
          <span className="period">{plan.pricePeriod}</span>
        </div>
      </div>

      {/* Pay Today Section */}
      <div className="pay-today-section">
        <div className="pay-today-label">Pay today</div>
        <div className="pay-today-amount">
          {formatCurrency(pricing.finalTotal)}
        </div>
        {pricing.hasOffer && pricing.originalTotal !== pricing.finalTotal && (
          <div className="pay-today-original">
            {formatCurrency(pricing.originalTotal)}
          </div>
        )}
      </div>

      {/* Features List */}
      <ul className="features-list">
        {visibleFeatures.map((feature, index) => (
          <li key={index}>{feature}</li>
        ))}
      </ul>

      {/* Show More/Less Button */}
      {hasMoreFeatures && (
        <button
          type="button"
          className="show-more-features-btn"
          onClick={() => setShowAllFeatures(!showAllFeatures)}
        >
          {showAllFeatures
            ? "Show less ↑"
            : `Show ${plan.features.length - VISIBLE_FEATURES_COUNT} more ↓`}
        </button>
      )}

      {/* Trial Activation Button (Solo only) */}
      {showTrialButton && (
        <Button
          type="default"
          size="large"
          block
          onClick={onActivateTrial}
          className="pricing-cta-trial"
          style={{ marginBottom: "0.5rem" }}
        >
          Activate {SUBSCRIPTION_CONFIG.TRIAL_DAYS}-Day Trial
        </Button>
      )}

      {/* Main CTA Button */}
      <Button
        type="primary"
        size="large"
        block
        onClick={onSelect}
        disabled={disabled}
        className="pricing-cta"
      >
        {getButtonText()}
      </Button>
    </div>
  );
};

export default SubscriptionPageContent;
